// @flow
import React, { Component } from 'react';
import { StyleSheet, View, TouchableOpacity, Keyboard } from 'react-native';
import { Text, ProgressBar } from 'trpc-react-native-ui';
import { Field } from 'redux-form';

import HoshiInput from '../common/HoshiInput';
import Icon from '../common/Icon';
import Checkbox from '../common/Checkbox';
import I18n from '../../lib/i18n';
import PasswordService from '../../lib/password';
import theme from '../../theme-config';
import config from '../../config';

class LoginForm extends Component<Props, State> {
  styles = getStyles();
  passwordInput: any;
  state = {
    acceptTermsCheckboxValue: false,
    passwordStrengthIndicators: null,
    isVisible: false,
  };

  isPasswordStrengthIndicatorsEmpty = (indicatorsList: Object) => {
    if (indicatorsList && Object.keys(indicatorsList).every(indicator => indicatorsList[indicator] === null)) {
      return true;
    }
    return false;
  };

  handlePasswordChange = (password: string) => {
    const indicators = PasswordService.strengthIndicators(password);
    this.setState({ passwordStrengthIndicators: indicators });
    this.props.onPasswordStrengthChange?.(indicators.progress);
  };

  togglePasswordVisible = () =>
    this.setState(state => ({
      isVisible: !state.isVisible,
    }));

  renderPasswordStrengthIndicator = () => {
    const indicators = this.state.passwordStrengthIndicators;
    if (!indicators || this.isPasswordStrengthIndicatorsEmpty(indicators)) return null;
    return (
      <View>
        <ProgressBar progress={indicators.progress} fillColor={indicators.color} />
        <View style={this.styles.suggestionsContainer}>
          {indicators.suggestions.map(suggestion => (
            <Text key={suggestion} style={this.styles.passwordSuggestion}>
              • {suggestion}
            </Text>
          ))}
        </View>
      </View>
    );
  };

  passwordFieldConfig = () => {
    if (!this.state.acceptTermsCheckboxValue && !!this.props.setCGUCheckboxValue) {
      return { returnKeyType: 'next', onSubmitEditing: Keyboard.dismiss };
    }
    return { returnKeyType: 'go', onSubmitEditing: this.props.handleSubmit };
  };

  createCheckboxHyperText = () => {
    return [
      { text: I18n.t('SignUp.CreateAccount.iAcceptTerms.part1') },
      {
        text: I18n.t('SignUp.CreateAccount.iAcceptTerms.hyperText'),
        url: config.STATIC_FILE_URL.replace('{fileName}', config.STATIC_FILE_NAME.privacyPolicy),
      },
    ];
  };

  render() {
    const styles = this.styles;
    const hyperText = this.createCheckboxHyperText();
    const passwordFieldConfig = this.passwordFieldConfig();
    const iconName = this.state.isVisible ? 'eye-with-line' : 'eye';
    const inputType = this.state.isVisible ? 'text' : 'password';
    const icon = (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={this.togglePasswordVisible}
        hitSlop={{ top: 5, bottom: 5, left: 5, right: 5 }}
        style={styles.touchableIconStyle}
      >
        <Icon name={iconName} size={17} color={theme.signUpInputTextColor} />
      </TouchableOpacity>
    );

    return (
      <View>
        {!this.props.withoutEmailField && (
          <Field
            autoFocus={this.props.autoFocus}
            name="email"
            component={HoshiInput}
            type="email"
            placeholder={I18n.t('email')}
            returnKeyType="next"
            style={styles.input}
            inputStyle={styles.textInput}
            onSubmitEditing={() => this.passwordInput.getRenderedComponent().focus()}
            testID={this.props.emailTestID}
          />
        )}
        <Field
          ref={ref => (this.passwordInput = ref)}
          name="password"
          component={HoshiInput}
          type={inputType}
          placeholder={I18n.t('password')}
          forwardRef
          returnKeyType={passwordFieldConfig.returnKeyType}
          onSubmitEditing={passwordFieldConfig.onSubmitEditing}
          style={styles.input}
          inputStyle={styles.textInput}
          icon={icon}
          onChangeText={(this.props.withPasswordStrength || !!this.props.onPasswordStrengthChange) && this.handlePasswordChange}
          testID={this.props.passwordTestID}
        />
        {this.props.withPasswordStrength ? this.renderPasswordStrengthIndicator() : null}
        {this.props.withTermsCheckbox && (
          <View>
            <Field
              name="acceptTerms"
              component={({ input: { value, onChange } }) => (
                <Checkbox
                  style={styles.checkbox}
                  labelStyle={styles.termsText}
                  onPress={() => {
                    onChange(!value);
                    if (!!this.props.setCGUCheckboxValue) {
                      this.props.setCGUCheckboxValue(!value);
                      this.setState({ acceptTermsCheckboxValue: !value });
                    }
                  }}
                  isChecked={value}
                  hyperText={hyperText}
                  lightColors
                  testID={'acceptTermsCheckbox'}
                />
              )}
            />
            <Field
              name="optionInternalCommercial"
              component={({ input: { value, onChange } }) => (
                <Checkbox
                  style={styles.checkbox}
                  labelStyle={styles.termsText}
                  onPress={() => onChange(!value)}
                  isChecked={value}
                  text={I18n.t('SignUp.CreateAccount.optionInternalCommercial')}
                  lightColors
                />
              )}
            />
            <Field
              name="optionExternalCommercial"
              component={({ input: { value, onChange } }) => (
                <Checkbox
                  style={styles.checkbox}
                  labelStyle={styles.termsText}
                  onPress={() => onChange(!value)}
                  isChecked={value}
                  text={I18n.t('SignUp.CreateAccount.optionExternalCommercial')}
                  lightColors
                />
              )}
            />
          </View>
        )}
      </View>
    );
  }
}

type State = {
  isVisible: boolean,
  passwordStrengthIndicators: ?{
    color: string,
    progress: number,
    warning: string,
    suggestions: string[],
  },
  acceptTermsCheckboxValue: boolean,
};

type Props = {
  withTermsCheckbox?: boolean,
  handleSubmit: Function,
  withPasswordStrength?: boolean,
  withoutEmailField?: boolean,
  autoFocus?: boolean,
  emailTestID?: string,
  passwordTestID?: string,
  setCGUCheckboxValue?: Function,
  onPasswordStrengthChange?: (progress: number) => void,
};

function getStyles() {
  return StyleSheet.create({
    checkbox: {
      paddingVertical: 8,
    },
    termsText: {
      fontSize: 14,
      lineHeight: 15,
      textAlign: 'left',
    },
    input: {
      marginVertical: 8,
    },
    textInput: {
      height: 50,
    },
    passwordSuggestion: {
      marginTop: 5,
      color: theme.errorColor,
      fontSize: 12,
      backgroundColor: 'transparent',
      textAlign: 'left',
    },
    suggestionsContainer: {
      minHeight: 50,
    },
    touchableIconStyle: {
      backgroundColor: 'transparent',
      padding: 5,
    },
  });
}

export default LoginForm;
